/* ============================================================
   One place that owns which editor is open, so a row anywhere can ask for one
   without every page carrying dialog state. Mounted once in AppShell.
   ============================================================ */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { type Habit, type Task } from '@habishine/client-core';

import { HabitEditor } from '../components/HabitEditor';
import { ProjectEditor, TagEditor } from '../components/ScopeEditors';
import { HabitSheet, TaskSheet } from '../components/Sheets';
import { TaskEditor } from '../components/TaskEditor';

type Open =
  | { kind: 'task'; task: Task }
  | { kind: 'habit'; habit: Habit | null }
  | { kind: 'project'; projectId: string | null }
  | { kind: 'tag'; tagId: string }
  | { kind: 'taskSheet'; taskId: string; day: string }
  | { kind: 'habitSheet'; habitId: string; day: string }
  | null;

interface Editors {
  editTask: (task: Task) => void;
  editHabit: (habit: Habit | null) => void;
  editProject: (projectId: string | null) => void;
  editTag: (tagId: string) => void;
  /** the action sheet a row opens */
  openTask: (taskId: string, day: string) => void;
  openHabit: (habitId: string, day: string) => void;
}

const Ctx = createContext<Editors | null>(null);

export function EditorsProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState<Open>(null);
  const close = useCallback(() => setOpen(null), []);
  const value = useMemo<Editors>(
    () => ({
      editTask: (task) => setOpen({ kind: 'task', task }),
      editHabit: (habit) => setOpen({ kind: 'habit', habit }),
      editProject: (projectId) => setOpen({ kind: 'project', projectId }),
      editTag: (tagId) => setOpen({ kind: 'tag', tagId }),
      openTask: (taskId, day) => setOpen({ kind: 'taskSheet', taskId, day }),
      openHabit: (habitId, day) => setOpen({ kind: 'habitSheet', habitId, day }),
    }),
    [],
  );
  return (
    <Ctx.Provider value={value}>
      {children}
      {open?.kind === 'task' ? <TaskEditor task={open.task} onClose={close} /> : null}
      {open?.kind === 'habit' ? <HabitEditor habit={open.habit} onClose={close} /> : null}
      {open?.kind === 'project' ? <ProjectEditor projectId={open.projectId} onClose={close} /> : null}
      {open?.kind === 'tag' ? <TagEditor tagId={open.tagId} onClose={close} /> : null}
      {open?.kind === 'taskSheet' ? <TaskSheet taskId={open.taskId} day={open.day} onClose={close} /> : null}
      {open?.kind === 'habitSheet' ? <HabitSheet habitId={open.habitId} day={open.day} onClose={close} /> : null}
    </Ctx.Provider>
  );
}

export function useEditors(): Editors {
  const v = useContext(Ctx);
  if (!v) throw new Error('useEditors must be used within EditorsProvider');
  return v;
}
